<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { p1Api } from '@/lib/p1-api';
import { p4Api, type FundingExceptionRecord, type LoanOffsetRecord } from '@/lib/p4-api';

const router = useRouter();
const exceptions = ref<FundingExceptionRecord[]>([]);
const offsets = ref<LoanOffsetRecord[]>([]);
const reasons = ref<Record<string, string>>({});
const loading = ref(true);
const submitting = ref(false);
const error = ref('');
const message = ref('');
const tab = ref<'OPEN' | 'HISTORY'>('OPEN');

const openExceptions = computed(() => exceptions.value.filter((item) => item.status === 'OPEN'));
const visibleExceptions = computed(() =>
  tab.value === 'OPEN'
    ? openExceptions.value
    : exceptions.value.filter((item) => item.status !== 'OPEN'),
);
const postedOffsets = computed(() => offsets.value.filter((item) => item.status === 'POSTED'));

onMounted(load);

async function load() {
  loading.value = true;
  error.value = '';
  try {
    const me = await p1Api.me();
    if (me.mustChangePassword) return void (await router.replace('/change-password'));
    if (!me.permissions.includes('loan-offset:resolve')) {
      error.value = '当前账号没有借款抵扣异常处置权限';
      return;
    }
    exceptions.value = await p4Api.fundingExceptions();
    offsets.value = await p4Api.loanOffsets().catch(() => []);
  } catch (cause) {
    const detail = cause instanceof Error ? cause.message : '借款抵扣工作台加载失败';
    if (detail.includes('登录已失效')) return void (await router.replace('/login'));
    error.value = detail;
  } finally {
    loading.value = false;
  }
}

async function returnToApplicant(item: FundingExceptionRecord) {
  const reason = reasons.value[item.id]?.trim() ?? '';
  if (reason.length < 2) {
    error.value = '退回申请人前必须填写至少 2 个字的处理意见';
    return;
  }
  submitting.value = true;
  error.value = '';
  message.value = '';
  try {
    await p4Api.returnFundingException(item.id, {
      exceptionVersion: item.version,
      documentVersion: item.document.version,
      reason,
    });
    message.value = '单据已退回申请人，借款抵扣方案可重新调整后提交';
    await load();
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : '异常处置失败';
  } finally {
    submitting.value = false;
  }
}

function money(value: string) {
  return new Intl.NumberFormat('zh-CN', { style: 'currency', currency: 'CNY' }).format(
    Number(value),
  );
}
function dateTime(value: string | null) {
  return value
    ? new Intl.DateTimeFormat('zh-CN', {
        timeZone: 'Asia/Shanghai',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(value))
    : '—';
}
</script>

<template>
  <main class="workspace-shell payment-shell">
    <header class="workspace-header">
      <RouterLink class="workspace-brand" to="/documents">
        <span class="brand-mark" aria-hidden="true">郎</span>
        <span><strong>郎报通</strong><small>借款抵扣</small></span>
      </RouterLink>
      <nav aria-label="主导航">
        <RouterLink to="/documents">我的单据</RouterLink>
        <RouterLink to="/approval-tasks">审批待办</RouterLink>
        <RouterLink to="/payment-tasks">付款处理</RouterLink>
        <RouterLink to="/payment-corrections">付款更正</RouterLink>
        <RouterLink to="/repayment-collections">还款确认</RouterLink>
        <RouterLink class="active" to="/loan-offsets">借款抵扣</RouterLink>
      </nav>
      <RouterLink class="text-link" to="/login">切换账号</RouterLink>
    </header>

    <section class="page-heading">
      <div>
        <p class="eyebrow">P4 · 借款抵扣账</p>
        <h1>借款抵扣与异常处置</h1>
        <p>最终审批时余额不足的单据不会生成付款任务，由财务退回申请人调整抵扣方案。</p>
      </div>
      <button class="quiet-button" type="button" :disabled="loading" @click="load">刷新</button>
    </section>

    <div class="approval-tabs payment-tabs" role="tablist" aria-label="借款抵扣视图">
      <button type="button" :class="{ active: tab === 'OPEN' }" @click="tab = 'OPEN'">
        待处理异常（{{ openExceptions.length }}）
      </button>
      <button type="button" :class="{ active: tab === 'HISTORY' }" @click="tab = 'HISTORY'">
        处理与抵扣历史
      </button>
    </div>

    <p v-if="error" class="inline-alert inline-alert--error" role="alert">{{ error }}</p>
    <p v-if="message" class="inline-alert" role="status">{{ message }}</p>
    <section v-if="loading" class="document-empty">正在加载借款抵扣记录…</section>

    <section v-else class="approval-list correction-list">
      <article
        v-for="item in visibleExceptions"
        :key="item.id"
        class="form-section correction-card"
      >
        <div class="section-heading-row">
          <div>
            <p class="eyebrow">{{ item.operatingOrg.name }} · {{ item.document.applicant.name }}</p>
            <h2>{{ item.document.documentNo }}</h2>
          </div>
          <span class="status-pill" :data-status="item.status">{{ item.status }}</span>
        </div>
        <dl class="payment-facts">
          <div>
            <dt>报销金额</dt>
            <dd>{{ money(item.document.requestedAmount) }}</dd>
          </div>
          <div>
            <dt>拟抵扣金额</dt>
            <dd>{{ money(item.detail.requestedAmount) }}</dd>
          </div>
          <div>
            <dt>锁定时余额</dt>
            <dd>{{ money(item.detail.availableBalance) }}</dd>
          </div>
          <div>
            <dt>异常时间</dt>
            <dd>{{ dateTime(item.createdAt) }}</dd>
          </div>
        </dl>
        <p>
          抵扣单 {{ item.detail.offsetNo }}，借款单
          {{
            item.document.reimbursementOffsets.find((offset) => offset.id === item.detail.offsetId)
              ?.loanDocument.documentNo ?? item.detail.loanDocumentId
          }}。
        </p>
        <form
          v-if="item.status === 'OPEN'"
          class="payment-form"
          @submit.prevent="returnToApplicant(item)"
        >
          <label
            ><span>退回处理意见</span
            ><textarea v-model="reasons[item.id]" maxlength="1000" rows="3" required />
          </label>
          <button class="danger-button" type="submit" :disabled="submitting">退回申请人调整</button>
        </form>
        <p v-else class="section-hint">
          {{ item.resolvedBy?.name ?? '系统' }} · {{ dateTime(item.resolvedAt) }} ·
          {{ item.resolutionNote }}
        </p>
      </article>

      <template v-if="tab === 'HISTORY'">
        <article v-for="item in postedOffsets" :key="item.id" class="form-section correction-card">
          <div class="section-heading-row">
            <div>
              <p class="eyebrow">{{ item.offsetNo }}</p>
              <h2>{{ item.reimbursementDocument.documentNo }}</h2>
            </div>
            <span class="status-pill" data-status="POSTED">已入账</span>
          </div>
          <dl class="payment-facts">
            <div>
              <dt>来源借款</dt>
              <dd>{{ item.loanDocument.documentNo }}</dd>
            </div>
            <div>
              <dt>抵扣金额</dt>
              <dd>{{ money(item.amount) }}</dd>
            </div>
            <div>
              <dt>入账时间</dt>
              <dd>{{ dateTime(item.postedAt) }}</dd>
            </div>
          </dl>
        </article>
      </template>
      <div
        v-if="!visibleExceptions.length && (tab === 'OPEN' || !postedOffsets.length)"
        class="document-empty"
      >
        <strong>{{ tab === 'OPEN' ? '当前没有待处理异常' : '暂无借款抵扣历史' }}</strong>
      </div>
    </section>
  </main>
</template>
